import { Download, FileUp, ShieldCheck, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { fmtDate, fmtMoney } from '../../../shared/format'
import {
  downloadBankStatement,
  MAX_STATEMENT_BYTES,
  STATEMENT_ACCEPT,
  useBankStatements,
  useDeleteBankStatement,
  useStatementPerms,
  useUploadBankStatement,
  type BankStatement,
  type StatementTarget,
} from '../../bankStatements'
import type { CardScope } from '../../credit'
import { monthLabel, toastError } from '../../ui'
import { Footer } from '../AccountForms'
import { Field, FormSection } from '../FormBits'

const AMOUNT = /^(\d+([.,]\d{1,2})?)?$/
const kb = (n: number) =>
  n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`

/**
 * Bankadan gelen ekstreler: dönem, bankanın bildirdiği borç/asgari ve sistemdeki karşılığıyla fark. Dosyalar sunucuda
 * şifreli; açma/yükleme/silme ayrı yetkiler (kişisel kayıtta sahibi).
 */
export function BankStatementsPanel({ type, id, scope }: { type: StatementTarget; id: string; scope: CardScope }) {
  const { t } = useTranslation()
  const perms = useStatementPerms(scope)
  const list = useBankStatements(type, id, perms.read)
  const remove = useDeleteBankStatement(type, id)
  const [uploading, setUploading] = useState(false)
  if (!perms.read) return <p className="text-muted-foreground text-sm">{t('finance.bankStatements.noAccess')}</p>

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <ShieldCheck className="size-3.5" aria-hidden />
          {t(scope === 'personal' ? 'finance.bankStatements.hintPersonal' : 'finance.bankStatements.hintCompany')}
        </p>
        {perms.upload ? (
          <Button size="sm" onClick={() => setUploading(true)}>
            <FileUp />
            {t('finance.bankStatements.upload')}
          </Button>
        ) : null}
      </div>
      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.isLoading ? (
        <Skeleton className="h-24" />
      ) : !list.data?.length ? (
        <EmptyState icon={FileUp} title={t('finance.bankStatements.empty')} />
      ) : (
        <ul className="divide-y rounded-lg border">
          {list.data.map((st) => (
            <li key={st.id} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-3 py-2.5 text-sm">
              <div className="min-w-0 flex-1 basis-48 space-y-0.5">
                <p className="font-medium capitalize">{monthLabel(st.period.slice(0, 7))}</p>
                <p className="text-muted-foreground truncate text-xs" title={st.fileName}>
                  {st.fileName} · {kb(st.size)}
                </p>
                {st.statementDate || st.dueDate ? (
                  <p className="text-muted-foreground text-xs">
                    {st.statementDate
                      ? t('finance.bankStatements.statementOn', { date: fmtDate(st.statementDate) })
                      : ''}
                    {st.statementDate && st.dueDate ? ' · ' : ''}
                    {st.dueDate ? t('finance.bankStatements.dueOn', { date: fmtDate(st.dueDate) }) : ''}
                  </p>
                ) : null}
                {st.note ? <p className="text-muted-foreground text-xs">{st.note}</p> : null}
              </div>
              <Compare st={st} />
              <div className="flex gap-1">
                {perms.download ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t('finance.bankStatements.download')}
                    title={t('finance.bankStatements.download')}
                    onClick={() => void downloadBankStatement(st).catch(toastError)}
                  >
                    <Download className="size-4" />
                  </Button>
                ) : null}
                {perms.delete ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t('common.delete')}
                    title={t('common.delete')}
                    disabled={remove.isPending}
                    onClick={() =>
                      void confirmModal({
                        title: t('finance.bankStatements.deleteTitle'),
                        description: t('finance.bankStatements.deleteBody', { name: st.fileName }),
                        confirmLabel: t('common.delete'),
                        destructive: true,
                      }).then(
                        (ok) =>
                          ok &&
                          remove.mutate(st.id, {
                            onSuccess: () => toast.success(t('finance.bankStatements.deleted')),
                            onError: toastError,
                          }),
                      )
                    }
                  >
                    <Trash2 className="size-4" />
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {uploading ? <UploadModal type={type} id={id} onDone={() => setUploading(false)} /> : null}
    </div>
  )
}

/** Bankanın tutarı ile sistemdeki karşılığı; fark varsa vurgulanır. */
function Compare({ st }: { st: BankStatement }) {
  const { t } = useTranslation()
  if (!st.bankTotal && !st.bankMin) return null
  const row = (label: string, bank: string | null, system: string | null, diff: string | null) =>
    bank ? (
      <>
        <dt className="text-muted-foreground">{label}</dt>
        <dd className="tabular-nums">{fmtMoney(bank)}</dd>
        <dd className="text-muted-foreground tabular-nums">{system ? fmtMoney(system) : '—'}</dd>
        <dd className={Number(diff) ? 'text-destructive font-medium tabular-nums' : 'tabular-nums'}>
          {diff ? fmtMoney(diff) : '—'}
        </dd>
      </>
    ) : null
  return (
    <div className="space-y-1">
      <dl className="grid grid-cols-[auto_auto_auto_auto] gap-x-3 gap-y-0.5 text-xs">
        <span />
        <span className="text-muted-foreground">{t('finance.bankStatements.bank')}</span>
        <span className="text-muted-foreground">{t('finance.bankStatements.system')}</span>
        <span className="text-muted-foreground">{t('finance.bankStatements.diff')}</span>
        {row(t('finance.bankStatements.total'), st.bankTotal, st.systemTotal, st.diffTotal)}
        {row(t('finance.bankStatements.min'), st.bankMin, st.systemMin, st.diffMin)}
      </dl>
      {st.match === null ? (
        <Badge tone="neutral">{t('finance.bankStatements.noSystem')}</Badge>
      ) : st.match ? (
        <Badge tone="success">{t('finance.bankStatements.match')}</Badge>
      ) : (
        <Badge tone="danger">{t('finance.bankStatements.mismatch')}</Badge>
      )}
    </div>
  )
}

function UploadModal({ type, id, onDone }: { type: StatementTarget; id: string; onDone: () => void }) {
  const { t } = useTranslation()
  const upload = useUploadBankStatement(type, id)
  const [file, setFile] = useState<File | null>(null)
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7))
  const [statementDate, setStatementDate] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [bankTotal, setBankTotal] = useState('')
  const [bankMin, setBankMin] = useState('')
  const [note, setNote] = useState('')
  const [touched, setTouched] = useState(false)
  const errors = {
    file: !file
      ? t('finance.bankStatements.form.fileRequired')
      : file.size > MAX_STATEMENT_BYTES
        ? t('finance.bankStatements.form.tooBig')
        : '',
    period: !/^\d{4}-\d{2}$/.test(period) ? t('finance.bankStatements.form.periodRequired') : '',
    amounts: !AMOUNT.test(bankTotal.trim()) || !AMOUNT.test(bankMin.trim()) ? t('finance.form.amountInvalid') : '',
  }
  const valid = Object.values(errors).every((e) => !e)
  const err = (f: keyof typeof errors) => (touched ? errors[f] || undefined : undefined)

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onDone()}
      dismissible={!upload.isPending}
      icon={<FileUp />}
      title={t('finance.bankStatements.form.title')}
      description={t('finance.bankStatements.form.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        setTouched(true)
        if (!valid || !file) return
        upload.mutate(
          { file, period, statementDate, dueDate, bankTotal, bankMin, note },
          {
            onSuccess: () => {
              toast.success(t('finance.bankStatements.uploaded'))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={upload.isPending} onCancel={onDone} />}
    >
      <FormSection>
        <Field
          id="bs-file"
          label={t('finance.bankStatements.form.file')}
          error={err('file')}
          hint={t('finance.bankStatements.form.fileHint')}
          full
        >
          <Input
            id="bs-file"
            type="file"
            accept={STATEMENT_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
        <Field id="bs-period" label={t('finance.bankStatements.form.period')} error={err('period')}>
          <Input id="bs-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </Field>
        <Field id="bs-st" label={t('finance.bankStatements.form.statementDate')}>
          <DateField id="bs-st" value={statementDate} onChange={setStatementDate} />
        </Field>
        <Field id="bs-due" label={t('finance.bankStatements.form.dueDate')}>
          <DateField id="bs-due" value={dueDate} onChange={setDueDate} />
        </Field>
        <Field
          id="bs-total"
          label={t('finance.bankStatements.form.bankTotal')}
          error={err('amounts')}
          hint={t('finance.bankStatements.form.compareHint')}
        >
          <Input id="bs-total" inputMode="decimal" value={bankTotal} onChange={(e) => setBankTotal(e.target.value)} />
        </Field>
        <Field id="bs-min" label={t('finance.bankStatements.form.bankMin')}>
          <Input id="bs-min" inputMode="decimal" value={bankMin} onChange={(e) => setBankMin(e.target.value)} />
        </Field>
        <Field id="bs-note" label={t('finance.bankStatements.form.note')} full>
          <Input id="bs-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </FormSection>
    </Modal>
  )
}
